import React, { Fragment, useState, Suspense } from 'react';
import clsx from 'clsx';
import {
  AddButton,
  ConfirmButtonArray,
  Disabled,
  FormikTextInput,
  SectionsSelect,
  Select
} from '@td-client-frontend/common';
import { Add } from '@td-client-frontend/icons';
import { FieldArray, Formik, FormikHelpers } from 'formik';
import { pullAt, startCase } from 'lodash';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { useDebounce } from 'use-debounce';
import * as Yup from 'yup';
import { Edge, WithInfinityQuery } from '../../../@types/common';
import { ManageRoleType } from '../../../@types/Roles';

import { Button } from '../../../components/Button';
import LoadingFallback from '../../../components/LoadingFallback';
import { ModalFooterButtonGroup } from '../../../components/Modal/NewModal';
import { useAxios } from '../../../context/AxiosContext';
import { useOrgsList } from '../../../hooks/useOrgsList';
import useRoles from '../../../hooks/useRoles';
import { OrganizationAndRolesList } from './components/OrganizationsAndRolesList';
import { contextTypeMapping, ManageUserRoleTask } from '../Manage/ManageUserModal';
import { FormValues, OrgAndRoleContext, User, UserRelation, UserRole } from '../../../@types/Users';
import { trimmingFields } from '../../../utils/normalizingUtils';

export const NewUserModal = ({ onRequestClose }: { onRequestClose: () => void }) => {
  const queryCache = useQueryClient();
  const { authAxios } = useAxios();
  const [newUsers, setNewUsers] = useState<User[]>([{ firstName: '', lastName: '', email: '' }]);
  const [relationsList, setRelationsList] = useState<UserRelation[]>([]);

  const [currentRole, setCurrentRole] = useState<string>('');
  const [currentOrganization, setCurrentOrganization] = useState<string>('');
  const [orgSearchText, setOrgSearchText] = useState<string>('');
  const [debouncedOrgSearch] = useDebounce<string>(orgSearchText, 750);
  const { data: roles } = useRoles();
  const orgsQuery = useOrgsList({
    search: debouncedOrgSearch.length ? debouncedOrgSearch : undefined
  });
  const { data: orgs } = orgsQuery;

  const getFormValidationSchema = () => {
    return Yup.object({
      userList: Yup.array().of(
        Yup.object().shape({
          firstName: Yup.string().required('First Name is required'),
          lastName: Yup.string().required('Last Name is required'),
          email: Yup.string()
            .email('Email is invalid')
            .required('Required')
            .test('checkEmailUnique', 'This email is already registered.', async (value) => {
              if (value && value.length >= 4) {
                const { data } = await authAxios.post<any>('/users/client-reps/validate-email', { email: value });
                return !!data?.available;
              }
              return true;
            })
        })
      )
    });
  };

  const createUsers = async ({ usersList }: any) => {
    try {
      const {
        data: { clientReps }
      } = await authAxios.post('/users/client-reps', {
        users: usersList.map((user: any) => trimmingFields(user))
      });

      // add relations to clients after creation
      const relationReqs = clientReps.map(({ id }: { id: string }) => {
        const createRolesTasks: ManageUserRoleTask[] = relationsList.reduce(
          (acc: ManageUserRoleTask[], { context, role }) => {
            acc.push({
              action: 'ADD',
              // @ts-ignore
              roleId: role.id ? Number(role.id) : undefined,
              contextId: context.id ? Number(context.id) : undefined,
              contextType: contextTypeMapping[context.contextType] || undefined
            });
            return acc;
          },
          []
        );

        return authAxios.post(`/users/ClientRep/${id}/role`, {
          roles: createRolesTasks
        });
      });

      await Promise.all(relationReqs);
      queryCache.invalidateQueries('client-reps');
      toast.success(`Successfully created ${usersList.length > 1 ? 'users' : 'user'} with roles`, {
        position: 'top-center'
      });

      onRequestClose();
    } catch (e: any) {
      const message = e?.response?.data?.errors?.length
        ? e.response.data.errors[0]
        : 'Please verify the information is correct and try again.';
      return toast.error(message, { position: 'top-center' });
    }
  };

  // flatten roles for use in select dropdown -- only active roles
  const rolesList =
    roles?.pages
      ?.filter((page) => page?.edges)
      .reduce(
        (result: Edge<ManageRoleType>[], page: WithInfinityQuery<ManageRoleType>) =>
          result.concat(page?.edges?.filter(({ node }) => node.active)),
        []
      ) ?? [];
  const orgsList = orgs
    ? Object.keys(orgs).map((key) => ({
        label: startCase(key),
        items: orgs[key].map(({ id, name, legacyGroupId }: any) => ({
          value: `${key}/${id}`,
          label: `${legacyGroupId !== undefined ? `(${legacyGroupId})` : ''} ${name}`
        }))
      }))
    : [];

  const addRelation = (values: FormValues) => {
    const [contextType, contextId] = (currentOrganization || '').split('/');
    const role: any = rolesList?.find(({ node: { id } }) => id === currentRole)?.node;
    const context = orgs?.[contextType]?.find(({ id }: any) => id === contextId);

    const relationExistsInList = relationsList.some(
      ({ role: role_, context: context_ }: any) =>
        role_.id === role?.id && context_.id === context?.id && context_.type === context?.type
    );

    if (relationExistsInList) {
      return toast.error('Role with such context already exists in the list...', { position: 'top-center' });
    }

    const relations = relationsList.concat([
      {
        context: {
          ...context,
          contextType
        },
        role
      }
    ]);

    setNewUsers([...values.userList]);
    setRelationsList(relations);
    setCurrentOrganization('');
    setCurrentRole('');
    setOrgSearchText('');
  };

  const removeRelation = ({
    values,
    role,
    context
  }: {
    values: FormValues;
    role: UserRole;
    context: OrgAndRoleContext;
  }) => {
    const filtered = relationsList.filter(
      ({ role: listItemRole, context: listItemContext }) =>
        listItemRole.id !== role.id ||
        listItemContext.id !== context.id ||
        listItemContext.contextType !== context.contextType
    );

    setNewUsers([...values.userList]);
    setRelationsList([...filtered]);
  };

  const onChangeUsers = ({ values, setValues }: { values: FormValues; setValues: (values: FormValues) => void }) => {
    const users = [...values.userList];
    const numberOfUsers = values.userList.length + 1;
    for (let i = users.length; i < numberOfUsers; i++) {
      users.push({ firstName: '', lastName: '', email: '' });
    }
    // update dynamic form
    setValues({ ...values, userList: users });
  };

  const removeUserAtIndex = ({
    values,
    setValues,
    index
  }: {
    values: FormValues;
    setValues: (values: FormValues) => void;
    index: number;
  }) => {
    const users = [...values.userList];
    pullAt(users, index);
    // update dynamic form
    setValues({ ...values, userList: users });
  };

  return (
    <Formik
      initialValues={{ userList: newUsers }}
      validateOnBlur
      validateOnChange={false}
      validationSchema={getFormValidationSchema()}
      onSubmit={async (values, { setSubmitting }: FormikHelpers<any>) => {
        setSubmitting(true);
        await createUsers({ usersList: newUsers });
        setSubmitting(false);
      }}
    >
      {({ values, setValues, isSubmitting, handleSubmit, submitForm, isValidating }) => (
        <form className="flex flex-col w-full" onSubmit={handleSubmit}>
          <div className="flex flex-col flex-grow overflow-y-auto">
            <div className="w-full p-6 pt-4 pb-10 min-h-12">
              <div className="mb-2">
                <h3 className="mb-2">User Details</h3>
                <div className="flex justify-between pt-2 pb-4 text-center text-gray-50">
                  <p>
                    Add the user(s) for which these permissions will apply. The email address provided will be used as a
                    login ID
                  </p>
                  <p>* Required</p>
                </div>
              </div>
              <Suspense fallback={<LoadingFallback />}>
                <div style={{ maxHeight: '20em' }} className="pl-2 overflow-y-auto">
                  <FieldArray name="userList">
                    {() =>
                      values.userList.map((user: any, i: number) => (
                        <Fragment key={`${user}.${i}`}>
                          <h5 className="card-title">User {i + 1}</h5>
                          <div
                            className="grid grid-flow-col gap-4"
                            style={{
                              gridTemplateColumns: 'repeat(3, minmax(0, 1fr)) 0.5fr'
                            }}
                          >
                            <FormikTextInput
                              data-testid="firstNmInput"
                              label="First Name *"
                              name={`userList.${i}.firstName`}
                              type="text"
                              placeholder="Please enter first name"
                            />
                            <FormikTextInput
                              data-testid="lastNmInput"
                              label="Last Name *"
                              name={`userList.${i}.lastName`}
                              type="text"
                              placeholder="Please enter last name"
                            />
                            <FormikTextInput
                              data-testid="emailInput"
                              label="Email Address *"
                              name={`userList.${i}.email`}
                              type="email"
                              placeholder="Please enter email address"
                            />
                            <ConfirmButtonArray
                              id={`userList.${i}`}
                              tooltipOverrides={{
                                place: 'bottom',
                                arrowColor: '#5b2f91',
                                offset: { top: -10, left: 0 }
                              }}
                              isCapture
                              entityName="user"
                              className={clsx(
                                'flex flex-col items-center justify-center w-5 h-5 mt-12',
                                values.userList.length === 1 && 'hidden'
                              )}
                              onClick={() => removeUserAtIndex({ values, setValues, index: i })}
                            >
                              <Add transform="rotate(45)" className="fill-current hover:text-danger" aria-hidden />
                            </ConfirmButtonArray>
                          </div>
                        </Fragment>
                      ))
                    }
                  </FieldArray>

                  <div className="flex justify-center">
                    <AddButton onClick={() => onChangeUsers({ values, setValues })} label="Add another user" />
                  </div>
                </div>

                <div className="pt-4 mt-4 border-opacity-25 border-t-1 border-gray-50">
                  <>
                    <h2 className="mb-2">Organizations and Roles</h2>
                    {relationsList.length > 0 && (
                      <OrganizationAndRolesList
                        values={values}
                        organizationsList={relationsList}
                        removeRelation={removeRelation}
                      />
                    )}
                  </>

                  <p className="mt-3 mb-2 text-gray-40">
                    Select the organization(s) and level of permission you want to assign.
                  </p>

                  <div className="flex items-center gap-4">
                    <div className="z-30 w-74">
                      {/* @ts-ignore */}
                      <Select
                        name="role"
                        options={rolesList?.map(({ node: { id, roleNm } }: any) => ({
                          label: roleNm,
                          value: id
                        }))}
                        value={currentRole}
                        label="Select Role*"
                        placeholder="Please select role(s)"
                        onChange={(value: string) => setCurrentRole(value)}
                        error=""
                        maxVisible={3}
                        isError={false}
                        required
                      />
                    </div>
                    <Disabled disabled={!currentRole}>
                      <div className="w-74">
                        <SectionsSelect
                          name="organization"
                          options={orgsList}
                          onSearchTextChange={(value: string) => setOrgSearchText(value)}
                          value={!currentRole ? null : currentOrganization}
                          label="Select Organization"
                          placeholder="Please select organization"
                          onChange={(value: string) => setCurrentOrganization(value)}
                          error=""
                          isLoading={orgsQuery.isLoading}
                          maxVisible={3}
                          isError={false}
                          required
                        />
                      </div>
                    </Disabled>
                  </div>

                  <Disabled disabled={!currentRole}>
                    {currentOrganization ? (
                      <Button onClick={() => addRelation(values)} title="Add" disabled={!currentRole}>
                        Add
                      </Button>
                    ) : (
                      <ConfirmButtonArray
                        id="add_user_confirm"
                        onClick={() => addRelation(values)}
                        disabled={!currentRole}
                        className="btn btn-primary"
                        tooltipOverrides={{
                          place: 'top',
                          arrowColor: '#5b2f91',
                          offset: {},
                          isModalConfirm: true
                        }}
                        customization={{
                          tooltipText:
                            'You are about to grant global permissions (i.e. access to all groups) for this user. Press continue to confirm to make changes or cancel only after checking with your supervisor.',
                          actionCancelButtonLabel: 'Cancel',
                          actionButtonLabel: 'Continue'
                        }}
                      >
                        Add
                      </ConfirmButtonArray>
                    )}
                  </Disabled>
                </div>
              </Suspense>
            </div>
          </div>
          <ModalFooterButtonGroup>
            <Button title="Cancel" kind="secondary" onClick={onRequestClose} />

            <ConfirmButtonArray
              id="modal_save_confirm"
              onClick={submitForm}
              disabled={isSubmitting || !relationsList.length || isValidating}
              className="btn btn-primary"
              tooltipOverrides={{
                place: 'top',
                arrowColor: '#5b2f91',
                offset: {},
                isModalConfirm: true
              }}
              customization={{
                tooltipText: 'Are you sure you want to create users?',
                actionCancelButtonLabel: 'No',
                actionButtonLabel: 'Yes'
              }}
            >
              Save
            </ConfirmButtonArray>
          </ModalFooterButtonGroup>
        </form>
      )}
    </Formik>
  );
};
